import { useState } from "react";
import { Percent, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { TooltipButton } from "@/components/common/Tooltips";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const MIN_MOTIVO = 10;

// nov-2026 · Cambia la tasa ordinaria de un crédito SIN pagos y recalcula tabla, CAT y total a
// pagar con el mismo motor del desembolso (mismas fechas, monto y comisión). Solo admin de la
// entidad; el backend repite todos los candados (POST /creditos/{id}/modificar-tasa).
export function ModificarTasaButton({ credito, pagos, onDone }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [tasa, setTasa] = useState("");
  const [motivo, setMotivo] = useState("");
  const [busy, setBusy] = useState(false);

  if (user?.role !== "admin_entidad") return null;
  if (credito?.cancelado || ["Cancelado", "Liquidado", "Castigado"].includes(credito?.estatus)) return null;
  if ((pagos || []).some((p) => p?.estatus !== "Cancelado")) return null;
  const ai = credito?.amort_input || {};
  if (ai.fechas_engine !== "calendario" || ai.fechas_corregidas) return null;
  if (["factoraje", "arrendamiento"].includes(ai.motor)) return null;
  if (credito?.linea_id || credito?.cartera_migrada) return null;
  if (Number(credito?.dias_atraso) > 0) return null;

  const grupal = !!(credito?.es_grupal && credito?.integrantes?.length);

  const abrir = () => {
    setTasa(credito?.tasa_ordinaria != null ? String(credito.tasa_ordinaria) : "");
    setMotivo("");
    setOpen(true);
  };

  const aplicar = async () => {
    const t = Number(tasa);
    const m = motivo.trim();
    if (!(t > 0)) {
      toast.error("Captura la nueva tasa ordinaria anual");
      return;
    }
    if (m.length < MIN_MOTIVO) {
      toast.error(`Escribe el motivo del cambio (mínimo ${MIN_MOTIVO} caracteres)`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/creditos/${credito.id}/modificar-tasa`, { tasa_ordinaria: t, motivo: m });
      toast.success(
        `Tasa actualizada: ${data?.tasa_anterior}% → ${data?.tasa_nueva}%. CAT: ${data?.cat ?? "—"}%. ` +
        "Vuelve a descargar la tabla y los documentos para firma."
      );
      setOpen(false);
      setMotivo("");
      onDone && onDone();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.mensaje || "No se pudo modificar la tasa"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <TooltipButton
        tooltip="Cambia la tasa de un crédito sin pagos y recalcula tabla, CAT y total a pagar."
        data-testid="modificar-tasa-btn"
        variant="outline"
        size="sm"
        className="h-8 gap-1.5 px-2.5"
        onClick={abrir}
      >
        <Percent className="h-4 w-4" /> Modificar tasa
      </TooltipButton>

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="modificar-tasa-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Modificar tasa</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  Se recalculan la tabla de amortización, el CAT y el total a pagar con la nueva
                  tasa. Las fechas, el monto y la comisión no cambian. Solo para créditos sin pagos.
                </p>
                {grupal && <p>En créditos grupales la nueva tasa se aplica a todas las integrantes.</p>}
                <p>Después vuelve a descargar la tabla de amortización y las hojas para firma.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3">
            <p className="text-[13px] text-slate-500" data-testid="modificar-tasa-actual">
              Tasa actual: {credito?.tasa_ordinaria ?? "—"}% anual
            </p>
            <div>
              <Label className="mb-1 block text-xs">
                Nueva tasa ordinaria anual (%) <span className="text-red-500">*</span>
              </Label>
              <Input
                data-testid="modificar-tasa-input"
                type="number"
                min="0"
                step="0.01"
                value={tasa}
                onChange={(e) => setTasa(e.target.value)}
              />
            </div>
            <div>
              <Label className="mb-1 block text-xs">
                Motivo <span className="text-red-500">*</span>
              </Label>
              <Textarea
                data-testid="modificar-tasa-motivo"
                rows={2}
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="Ej. se capturó la tasa incorrecta; el Comité autorizó 150%"
              />
              <p className="mt-1 text-[11px] text-slate-400">
                Obligatorio, mínimo {MIN_MOTIVO} caracteres. Quedará en la bitácora de la entidad.
              </p>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy} data-testid="modificar-tasa-volver">Volver</AlertDialogCancel>
            <AlertDialogAction
              data-testid="modificar-tasa-confirmar"
              onClick={(e) => { e.preventDefault(); aplicar(); }}
              disabled={busy || !(Number(tasa) > 0) || motivo.trim().length < MIN_MOTIVO}
            >
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Aplicar cambio
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
